import {
  GUIDE_SAVE_VERSION, GuidePriority, GuideTiming, dwellMs, readMs, tipHoldMs,
  type GuideContext, type GuideSaveJson, type GuideScreen, type GuideScript, type GuideStep,
} from "./guide";
import {
  type ActiveView, type EndReason, type GuideSwitches, type QueueEnv, type QueueEvent, type Queued,
  candidates, firstSessionOpen, guarded, hidesShown, isSafety, isWalkthrough, stepText, suppressed,
} from "./guideSelect";

export type { ActiveView, EndReason, QueueEnv, QueueEvent } from "./guideSelect";

/**
 * The guide's queue (DESIGN §4.2): which step shows, when it starts, what ends it, and what is done. Pure and DOM-free:
 * time comes from the injected `now()`, the screen's facts from each GuideContext, and the host's state from QueueEnv.
 * guideController.ts turns its events into the overlay, the sim hold and the save.
 *
 * Rules, in short:
 * - One step at a time: the highest priority eligible step for the current screen, then registration order.
 * - A step starts only when the screen has been quiet (busy() null) for QuietGraceMs and StepGapMs after the last end.
 * - `do` steps end only when `until` holds; `info` on a tap after TapGuardMs (or `until`); `welcome` on its CTA; `tip`
 *   after tipHoldMs of visible, un-stalled time, its ✕, or `until`.
 * - Something that takes the screen while a step shows (a sheet, the pause layer: hidesShown) makes it step aside
 *   (hidden, its clock frozen) until clear again; a passing banner or callout only holds back a step's start.
 * - M45: the same goes for anything that covers the step's target or its screen (QueueEnv.clear, the overlay's one
 *   "is the guide's surface clear?" hit test, guideDom.ts surfaceClear): a step starts only on a clear surface, and a
 *   showing one steps aside once it has been covered CoverSettleMs, coming back when uncovered. A tip covered before its
 *   dwell (GuideTiming dwellMs) comes back; one already read goes with whatever covered it.
 * - M45 dwell: a showing tip gives way to a more important step only once it has been up its dwell (read), and to an
 *   urgent one (priority Safety and up: boss, flyers, bombers, the last stand) after MIN_SHOWN_MS; normal steps wait
 *   their turn. A tip that gave way unread is not done and comes back after. Between two steps: StepGapMs.
 * - A hint (not the walkthrough) starts only once its `when` has held WHEN_SETTLE_MS; a tip withdrawn unread (its screen
 *   went) waits ReshowAfterMs before it may show again.
 * - A signal that made a queued step eligible stays latched for it until it shows, so a banner that holds the step back
 *   does not lose the moment (`enemy:<id>` is a one-update signal).
 * - A `needsTarget` step counts as not wanted while its target is off screen (QueueEnv.ready); a `onePerVisit` step
 *   waits for the next visit to its screen once another has shown on this one.
 * - A line to read is done once it has been on screen long enough (an info card its reading time, a tip its dwell),
 *   even before it ends: a reload, a trip to another screen or a more important step that interrupts it then never
 *   shows it again. A `do` step stays until its goal holds, and the welcome until its button.
 * Which steps are eligible, and in what order, is guideSelect.ts.
 */

/** An evaluation gap longer than this counts as this much: the page was hidden or the pump throttled. */
const MAX_TICK_MS = 500;
/**
 * A step on screen stays at least this long before a more important one may push it off (a tip yielding): a line that
 * blinked up and away read as flicker (owner 2026-09-30). Also the least a line shows before it counts as said when it
 * yields, so it never comes back for a second, equally short showing.
 */
export const MIN_SHOWN_MS = 1500;
/**
 * A hint's `when` must have held this long before it starts (the walkthrough, which answers the player's own last move,
 * starts at once). A card dealt and picked straight away, or a raider on screen for a moment, flashed a line up only for
 * its `until` to take it down again (the headless probe logged such lines up 0.8 s, owner 2026-09-30).
 */
export const WHEN_SETTLE_MS = GuideTiming.QuietGraceMs;

interface Active {
  readonly step: GuideStep;
  readonly forced: boolean;
  /** When it last became visible (shown, or back from aside): the tap guard and the cue count from here. */
  visibleSince: number;
  /** Visible, un-stalled time so far (every kind: tips time out on it, lines to read count as read on it). */
  visibleMs: number;
  /** A tip's hold, fixed from its text when it showed. */
  readonly holdMs: number;
  /** Since when its target or screen has been covered (QueueEnv.clear false), or null. */
  coveredSince: number | null;
  aside: boolean;
  /** While aside: when busy last cleared (null while still busy). */
  quietSince: number | null;
  lastTick: number;
  text: string;
  /** Read (seenOnScreen): already marked done while it is still up. */
  seen: boolean;
}

/**
 * A line the player has had on screen long enough is done: it must not come back after a reload, a withdraw or a
 * yield. An info card counts once it has been up for its reading time; a tip once it has been up its dwell (M45: it
 * used to count after 2 s glanced, so a sheet opening over it at 2 s ate it). A `do` step teaches until its goal holds,
 * and the welcome card waits for its button.
 */
function seenOnScreen(step: GuideStep, visibleMs: number, text: string): boolean {
  if (step.kind === "tip") return visibleMs >= dwellMs(text, "tip");
  return step.kind === "info" && visibleMs >= Math.max(readMs(text), dwellMs(text, "info"));
}

/**
 * Urgent: a warning about a real threat (priority Safety: boss, flyers, bombers, the last stand), or the walkthrough
 * answering the player's own move, may cut in on a tip that has not had its dwell (still after MIN_SHOWN_MS).
 */
export function urgent(step: GuideStep): boolean { return (step.priority ?? GuidePriority.Tip) >= GuidePriority.Safety; }

export class GuideQueue {
  private readonly steps: GuideStep[] = [];
  private readonly scriptOf = new Map<string, string>();
  private done: Set<string>;
  private tipsOff: boolean;
  private walkthroughSkipped: boolean;
  private activeStep: Active | null = null;
  private readonly queued = new Map<string, Queued>();
  /** Dropped by maxWaitMs: out until its `when` goes false once. */
  private readonly dropped = new Set<string>();
  private quietSince: number | null = null;
  private lastEnd = -Infinity;
  private pendingCount = 0;
  private highestCleared = 0;
  private force: string | null = null;
  private readonly warned = new Set<string>();
  /** A `onePerVisit` step has shown since the screen last changed. */
  private visitSpent = false;
  /** Non-walkthrough steps withdrawn unread, and when: not shown again for ReshowAfterMs. */
  private readonly withdrawnAt = new Map<string, number>();
  /** Tips that have given way unread once (they come back once). */
  private readonly yielded = new Set<string>();

  constructor(save: GuideSaveJson, private readonly now: () => number, private readonly onChange: (save: GuideSaveJson) => void = () => {}) {
    this.done = new Set(save.done);
    this.tipsOff = save.tipsOff === true;
    this.walkthroughSkipped = save.walkthroughSkipped === true;
  }

  // ---------------------------------------------------------------------------------------------- scripts and state

  /** Adds a script's steps after those already registered; registering a script id again replaces its steps. */
  register(script: GuideScript): void {
    for (let i = this.steps.length - 1; i >= 0; i--) if (this.scriptOf.get(this.steps[i].id) === script.id) this.steps.splice(i, 1);
    for (const s of script.steps) { this.steps.push(s); this.scriptOf.set(s.id, script.id); }
  }

  step(id: string): GuideStep | undefined { return this.steps.find((s) => s.id === id); }
  get registered(): readonly GuideStep[] { return this.steps; }

  get save(): GuideSaveJson {
    const out: GuideSaveJson = { v: GUIDE_SAVE_VERSION, done: [...this.done] };
    if (this.tipsOff) out.tipsOff = true;
    if (this.walkthroughSkipped) out.walkthroughSkipped = true;
    return out;
  }

  isDone(id: string): boolean { return this.done.has(id); }
  get doneIds(): readonly string[] { return [...this.done]; }
  get tipsAreOff(): boolean { return this.tipsOff; }
  get skipped(): boolean { return this.walkthroughSkipped; }
  get active(): GuideStep | null { return this.activeStep?.step ?? null; }
  /** Steps eligible at the last evaluation but not yet shown. */
  get pending(): number { return this.pendingCount; }

  /** The walkthrough and the hub's first session go with "Skip tips" (guideSelect.ts). */
  isWalkthrough(step: GuideStep): boolean { return isWalkthrough(step); }
  /** A warning about a real threat, which no switch silences (guideSelect.ts). */
  isSafety(step: GuideStep): boolean { return isSafety(step); }
  /** Hidden by "Skip tips" or the tips switch (guideSelect.ts, DESIGN §4.2.1). */
  suppressed(step: GuideStep): boolean { return suppressed(step, this.switches); }
  private get switches(): GuideSwitches { return { tipsOff: this.tipsOff, walkthroughSkipped: this.walkthroughSkipped }; }

  /**
   * Other pop-ups may show: nothing on screen or queued, and the hub's first session (hub.firstWin … hub.play2) is over,
   * skipped, or outgrown (two levels cleared, however its steps went).
   */
  quiet(): boolean {
    if (this.activeStep !== null || this.pendingCount > 0) return false;
    return !firstSessionOpen((id) => this.scriptOf.has(id), this.done, this.walkthroughSkipped, this.highestCleared);
  }

  private mark(id: string): void {
    if (this.done.has(id)) return;
    this.done.add(id);
    this.onChange(this.save);
  }

  skipWalkthrough(): QueueEvent[] {
    if (!this.walkthroughSkipped) { this.walkthroughSkipped = true; this.onChange(this.save); }
    return this.endIfSuppressed();
  }

  setTipsOff(off: boolean): QueueEvent[] {
    if (this.tipsOff !== off) { this.tipsOff = off; this.onChange(this.save); }
    return this.endIfSuppressed();
  }

  /** "Practice battle with me": the walkthrough teaches again on the next level-1 run. */
  startPractice(): void {
    for (const s of this.steps) if (s.walkthrough === true) this.done.delete(s.id);
    this.walkthroughSkipped = false;
    this.onChange(this.save);
  }

  /** Debug "Reset guide": nothing done, both switches back on. */
  reset(): QueueEvent[] {
    const ev = this.activeStep ? [this.end("replaced", false)] : [];
    this.done.clear(); this.tipsOff = false; this.walkthroughSkipped = false;
    this.queued.clear(); this.dropped.clear();
    this.onChange(this.save);
    return ev;
  }

  /** Debug: show this step at the next evaluation, whatever its conditions. */
  forceShow(id: string): boolean {
    if (!this.step(id)) return false;
    this.force = id;
    return true;
  }

  /** A screen mounted: the next step waits QuietGraceMs from now, and a new visit may show a `onePerVisit` step. */
  screenChanged(): void { this.quietSince = null; this.visitSpent = false; }

  // ---------------------------------------------------------------------------------------------- ending

  private end(reason: EndReason, markDone: boolean): QueueEvent {
    const a = this.activeStep!;
    this.activeStep = null;
    this.lastEnd = this.now();
    this.queued.delete(a.step.id);
    const done = markDone && !a.forced;
    if (done) this.mark(a.step.id);
    return { type: "end", step: a.step, reason, done };
  }

  private endIfSuppressed(): QueueEvent[] {
    const a = this.activeStep;
    return a && !a.forced && this.suppressed(a.step) ? [this.end("suppressed", false)] : [];
  }

  /** A tap anywhere while an info step is up (after the tap guard): it continues. */
  tap(): QueueEvent[] {
    const a = this.activeStep;
    if (!a || a.aside || a.step.kind !== "info") return [];
    if (this.now() - a.visibleSince < GuideTiming.TapGuardMs) return [];
    return [this.end("tap", true)];
  }

  /** The welcome card's CTA or alt link, or a tip's ✕. */
  finish(reason: "cta" | "alt" | "close"): QueueEvent[] {
    return this.activeStep ? [this.end(reason, true)] : [];
  }

  /** The screen went away: its step is withdrawn, not done, and its queue forgotten (it is re-read on the next attach). */
  withdraw(screen: GuideScreen): QueueEvent[] {
    for (const s of this.steps) if (s.screen === screen) this.queued.delete(s.id);
    this.pendingCount = 0;
    const a = this.activeStep;
    if (!a || a.step.screen !== screen) return [];
    const ev = this.end("withdrawn", false);
    if (a.step.kind === "tip" && !isWalkthrough(a.step) && !this.done.has(a.step.id)) this.withdrawnAt.set(a.step.id, this.now());
    return [ev];
  }

  /** The step's surface is clear (QueueEnv.clear; absent or throwing reads as clear / covered respectively). */
  private clear(step: GuideStep, env: QueueEnv): boolean {
    if (!env.clear) return true;
    try { return env.clear(step); } catch { return false; }
  }

  // ---------------------------------------------------------------------------------------------- evaluation

  private safe(step: GuideStep, what: "when" | "until", f: (c: GuideContext) => boolean, ctx: GuideContext): boolean { return guarded(step, what, f, ctx, this.warned); }

  text(step: GuideStep, ctx: GuideContext | null): string { return stepText(step, ctx); }

  /** The candidates for `ctx.screen`, best first (guideSelect.ts); keeps the queued map, latches and drops up to date. */
  private candidates(ctx: GuideContext, env: QueueEnv): GuideStep[] {
    return candidates({
      steps: this.steps, active: this.activeStep?.step ?? null, done: this.done, switches: this.switches, visitSpent: this.visitSpent,
      now: this.now(), when: (step, c) => this.safe(step, "when", step.when, c),
    }, { queued: this.queued, dropped: this.dropped }, ctx, env);
  }

  /** Re-evaluate against the current screen's facts. `ctx` null: no screen attached (only the clocks move). */
  evaluate(ctx: GuideContext | null, env: QueueEnv): QueueEvent[] {
    const t = this.now();
    const ev: QueueEvent[] = [];
    if (ctx) this.highestCleared = ctx.highestCleared;
    if (env.busy === null) { if (this.quietSince === null) this.quietSince = t; } else this.quietSince = null;

    // A forced (debug) step replaces whatever is up, on any attached screen.
    if (this.force !== null) {
      const step = this.step(this.force)!;
      this.force = null;
      if (this.activeStep) ev.push(this.end("replaced", false));
      ev.push(this.start(step, ctx, true));
      return ev;
    }

    const a = this.activeStep;
    if (a) {
      const dt = Math.min(MAX_TICK_MS, Math.max(0, t - a.lastTick));
      a.lastTick = t;
      const sameScreen = ctx !== null && ctx.screen === a.step.screen;
      if (sameScreen) a.text = this.text(a.step, ctx);
      if (!a.forced && this.suppressed(a.step)) { ev.push(this.end("suppressed", false)); }
      else if (!a.forced && sameScreen && a.step.until && this.safe(a.step, "until", a.step.until, ctx!)) { ev.push(this.end("until", true)); }
      else {
        // Step aside while something else takes the screen (a sheet, the pause layer, the intro card: hidesShown, not a
        // passing banner or callout); come back once it has been clear for the grace.
        let covered = false;
        if (!hidesShown(env.busy) && !this.clear(a.step, env)) {
          a.coveredSince ??= t;
          covered = a.aside || t - a.coveredSince >= GuideTiming.CoverSettleMs;
        } else a.coveredSince = null;
        const hidden = hidesShown(env.busy) || covered;
        if (hidden && a.step.kind === "tip" && a.seen && !a.aside) {
          // A tip already read goes with the sheet rather than popping up a second time after it.
          ev.push(this.end("timeout", true));
        } else if (hidden) {
          a.quietSince = null;
          if (!a.aside) { a.aside = true; ev.push({ type: "aside", step: a.step }); }
        } else if (a.aside) {
          if (a.quietSince === null) a.quietSince = t;
          if (t - a.quietSince >= GuideTiming.QuietGraceMs) { a.aside = false; a.visibleSince = t; ev.push({ type: "back", step: a.step }); }
        } else if (env.visible) {
          a.visibleMs += dt;
          if (!a.forced && !a.seen && seenOnScreen(a.step, a.visibleMs, a.text)) { a.seen = true; this.mark(a.step.id); }
        }
        if (this.activeStep === a && a.step.kind === "tip" && !a.forced && a.visibleMs >= a.holdMs) ev.push(this.end("timeout", true));
      }
    }

    if (!ctx) { this.pendingCount = 0; return ev; }
    const cands = this.candidates(ctx, env);
    this.pendingCount = cands.length;
    const cur = this.activeStep;
    // A showing tip gives way to a more important step (never another tip) only once it has been read (its dwell), or
    // to an urgent warning once it has been up MIN_SHOWN_MS (no blink). Read, it counts as said; unread, it is not done
    // and comes back after (M45: a tip used to be replaced 1.5 s in, before anyone could read it).
    if (cur && cur.step.kind === "tip" && !cur.forced && !cur.aside && env.busy === null && cur.visibleMs >= MIN_SHOWN_MS) {
      const pr = cur.step.priority ?? GuidePriority.Tip;
      const top = cands.find((s) => s.kind !== "tip" && (s.priority ?? GuidePriority.Tip) > pr && (cur.seen || urgent(s)) && this.clear(s, env));
      // unread, it comes back once; a second interruption counts it as said (no tip bouncing in and out all battle)
      if (top) { const again = this.yielded.has(cur.step.id); this.yielded.add(cur.step.id); ev.push(this.end("yield", cur.seen || again)); }
    }
    if (this.activeStep || cands.length === 0) return ev;
    if (env.busy !== null || this.quietSince === null || t - this.quietSince < GuideTiming.QuietGraceMs) return ev;
    if (t - this.lastEnd < GuideTiming.StepGapMs) return ev;
    // the best candidate whose surface is clear (one covered by a sheet waits; it is not dropped)
    const next = cands.find((s) => this.reshowOk(s, t) && this.clear(s, env));
    if (!next) return ev;
    const since = this.queued.get(next.id)?.since ?? t;
    if (!isWalkthrough(next) && t - since < WHEN_SETTLE_MS) return ev;
    ev.push(this.start(next, ctx, false));
    this.pendingCount = cands.length - 1;
    return ev;
  }

  /** Not withdrawn unread within ReshowAfterMs (walkthrough steps always may: they answer the player's own move). */
  private reshowOk(step: GuideStep, t: number): boolean {
    const at = this.withdrawnAt.get(step.id);
    if (at === undefined) return true;
    if (t - at >= GuideTiming.ReshowAfterMs) { this.withdrawnAt.delete(step.id); return true; }
    return false;
  }

  private start(step: GuideStep, ctx: GuideContext | null, forced: boolean): QueueEvent {
    const t = this.now();
    const text = this.text(step, ctx);
    this.queued.delete(step.id);
    this.withdrawnAt.delete(step.id);
    if (step.onePerVisit === true && !forced) this.visitSpent = true;
    this.activeStep = {
      step, forced, visibleSince: t, visibleMs: 0,
      holdMs: tipHoldMs(text), coveredSince: null, aside: false, quietSince: null, lastTick: t, text, seen: false,
    };
    return { type: "show", step };
  }

  /** What the overlay should draw for the active step now. */
  view(): ActiveView | null {
    const a = this.activeStep;
    if (!a) return null;
    const t = this.now();
    const cueAt = Math.min(readMs(a.text), GuideTiming.ContinueCueMs);
    return {
      step: a.step, text: a.text, aside: a.aside, forced: a.forced,
      cue: a.step.kind === "info" && !a.aside && t - a.visibleSince >= cueAt,
      drain: a.step.kind === "tip" ? Math.max(0, 1 - a.visibleMs / a.holdMs) : null,
    };
  }
}
